import { useState, useCallback } from "react";
import apiClient from "../../../utils/apiClient";
import server from "../../../utils/serverConfig";
import toast from "react-hot-toast";

export default function useCRMOnboarding() {
    const [draftId, setDraftId] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [allowedBrokers, setAllowedBrokers] = useState([]);

    const [basicInfo, setBasicInfo] = useState({
        name: "",
        phone_number: "",
        email: "",
        pan_card: "",
        date_of_birth: "",
        product_is_optimus: false,
        product_is_pledgeplus: false,
        product_is_polaris: false,
        product_is_polaris_lite: false,
        source: "crm",
        broker: "",
    });

    const [legalities, setLegalities] = useState({
        terms_accepted: false,
        risk_disclosure_accepted: false,
        data_consent_accepted: false,
        kyc_completed: false,
        client_agreement_signed: false,
        nominee_registered: false,
        notes: "",
        leegality_doc_id: "",
        leegality_doc_url: "",
        is_leegality_doc_sent: false,
        is_legalities_complete: false,
        is_onhold_leegality: false,
        agreement_type: "authorisation",
        address_line_1: "",
        address_line_2: "",
    });

    const [selectedBroker, setSelectedBroker] = useState("");

    const resetOnboarding = useCallback(() => {
        setDraftId(null);
        setCurrentStep(1);
        setSelectedBroker("");
        setAllowedBrokers([]);
        setBasicInfo({
            name: "",
            phone_number: "",
            email: "",
            pan_card: "",
            date_of_birth: "",
            product_is_optimus: false,
            product_is_pledgeplus: false,
            product_is_polaris: false,
            product_is_polaris_lite: false,
            source: "crm",
            broker: "",
        });
        setLegalities({
            terms_accepted: false,
            risk_disclosure_accepted: false,
            data_consent_accepted: false,
            kyc_completed: false,
            client_agreement_signed: false,
            nominee_registered: false,
            notes: "",
            leegality_doc_id: "",
            leegality_doc_url: "",
            is_leegality_doc_sent: false,
            is_legalities_complete: false,
            is_onhold_leegality: false,
            agreement_type: "authorisation",
            address_line_1: "",
            address_line_2: "",
        });
    }, []);

    const createDraft = useCallback(async () => {
        setIsSaving(true);
        try {
            const response = await apiClient.post(`${server.godseye.baseUrl}crm/onboarding/create-draft`);
            if (response.data && response.data.id) {
                setDraftId(response.data.id);
                return response.data.id;
            }
        } catch (error) {
            console.error("Failed to create onboarding draft:", error);
            toast.error("Failed to initialize onboarding session");
        } finally {
            setIsSaving(false);
        }
        return null;
    }, []);

    const fetchAllowedBrokers = useCallback(async (products) => {
        if (!products || products.length === 0) {
            setAllowedBrokers([]);
            return;
        }
        try {
            const response = await apiClient.get(
                `${server.godseye.baseUrl}crm/onboarding/allowed-brokers?products=${products.join(",")}`
            );
            if (response.data && response.data.allowed) {
                setAllowedBrokers(response.data.allowed);
            }
        } catch (error) {
            console.error("Failed to fetch allowed brokers:", error);
        }
    }, []);

    const saveBasicInfo = useCallback(async (id, data) => {
        const targetId = id || draftId;
        setIsSaving(true);
        try {
            if (!targetId) {
                const response = await apiClient.post(`${server.godseye.baseUrl}crm/onboarding/create-draft`, data);
                if (response.data && response.data.id) {
                    setDraftId(response.data.id);
                    return true;
                }
                return false;
            } else {
                await apiClient.patch(
                    `${server.godseye.baseUrl}crm/onboarding/${targetId}/basic-info`,
                    data
                );
                return true;
            }
        } catch (error) {
            console.error("Failed to save basic info:", error);
            toast.error("Failed to save progress");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [draftId]);

    const saveLegalities = useCallback(async (id, data) => {
        const targetId = id || draftId;
        if (!targetId) return false;
        setIsSaving(true);
        try {
            // Exclude internally managed fields to prevent overwriting backend updates
            const {
                leegality_doc_id,
                leegality_doc_url,
                is_leegality_doc_sent,
                is_legalities_complete,
                ...prunedData
            } = data;

            await apiClient.patch(
                `${server.godseye.baseUrl}crm/onboarding/${targetId}/legalities`,
                prunedData
            );
            return true;
        } catch (error) {
            console.error("Failed to save legalities:", error);
            toast.error("Failed to save progress");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [draftId]);

    const saveBrokerDetails = useCallback(async (id, broker, clientId, brokerData) => {
        const targetId = id || draftId;
        if (!targetId) return false;
        setIsSaving(true);
        try {
            await apiClient.patch(
                `${server.godseye.baseUrl}crm/onboarding/${targetId}/broker-details`,
                {
                    broker,
                    client_id: clientId,
                    brokerData
                }
            );
            return true;
        } catch (error) {
            console.error("Failed to save broker details:", error);
            toast.error("Failed to complete onboarding save");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [draftId]);

    /**
     * Manually saves a Leegality document ID and its completion status.
     * Calls the dedicated /manual-leegality-doc endpoint which explicitly writes
     * leegality_doc_id, is_leegality_doc_sent, and is_legalities_complete.
     * This is intentionally separate from saveLegalities(), which never touches those fields.
     *
     * @param {string} id - draft ID
     * @param {string} docId - the Leegality document ID
     * @param {boolean} isComplete - whether the doc is confirmed signed
     * @returns {boolean} success
     */
    const saveManualDocId = useCallback(async (id, docId, isComplete = false) => {
        const targetId = id || draftId;
        if (!targetId) return false;
        if (!docId || !docId.trim()) {
            toast.error("Document ID cannot be empty");
            return false;
        }
        setIsSaving(true);
        try {
            await apiClient.patch(
                `${server.godseye.baseUrl}crm/onboarding/${targetId}/manual-leegality-doc`,
                {
                    leegality_doc_id: docId.trim(),
                    is_leegality_doc_sent: true,
                    is_legalities_complete: isComplete,
                }
            );
            return true;
        } catch (error) {
            console.error("Failed to save manual doc ID:", error);
            toast.error("Failed to save document ID");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [draftId]);


    const loadDraft = useCallback(async (id, preventStepChange = false) => {
        setIsSaving(true);
        try {
            const response = await apiClient.get(`${server.godseye.baseUrl}crm/onboarding/${id}`);
            if (response.data) {
                const data = response.data;
                setDraftId(data.id);
                setBasicInfo({
                    name: data.name || "",
                    phone_number: data.phone_number || "",
                    email: data.email || "",
                    pan_card: data.pan_card || "",
                    date_of_birth: data.date_of_birth || "",
                    product_is_optimus: data.product_is_optimus || false,
                    product_is_pledgeplus: data.product_is_pledgeplus || false,
                    product_is_polaris: data.product_is_polaris || false,
                    product_is_polaris_lite: data.product_is_polaris_lite || false,
                    source: data.source || "crm",
                    broker: data.broker || "",
                });
                setLegalities({
                    terms_accepted: data.terms_accepted || false,
                    risk_disclosure_accepted: data.risk_disclosure_accepted || false,
                    data_consent_accepted: data.data_consent_accepted || false,
                    kyc_completed: data.kyc_completed || false,
                    client_agreement_signed: data.client_agreement_signed || false,
                    nominee_registered: data.nominee_registered || false,
                    notes: data.notes || "",
                    leegality_doc_id: data.leegality_doc_id || "",
                    leegality_doc_url: data.leegality_doc_url || "",
                    is_leegality_doc_sent: data.is_leegality_doc_sent || false,
                    is_legalities_complete: data.is_legalities_complete || false,
                    is_onhold_leegality: data.is_onhold_leegality || false,
                    agreement_type: data.agreement_type || "authorisation",
                    address_line_1: data.address_line_1 || "",
                    address_line_2: data.address_line_2 || "",
                });
                const rawBroker = data.broker || "";
                const cleanBroker = rawBroker.toLowerCase().replace(/[^a-z0-9_]/g, "");
                let normalizedBroker = cleanBroker;
                if (cleanBroker === "dhanhq" || cleanBroker === "dhan_hq") {
                    normalizedBroker = "dhan_hq";
                }
                setSelectedBroker(normalizedBroker);

                // Determine the step to resume from
                if (!preventStepChange) {
                    if (!data.is_basic_info_complete) {
                        setCurrentStep(1);
                    } else if (data.is_onhold_leegality && data.broker) {
                        setCurrentStep(2);
                    } else if (!data.is_legalities_complete && !data.is_onhold_leegality) {
                        setCurrentStep(2);
                    } else {
                        setCurrentStep(3);
                    }
                }

                // Fetch allowed brokers if step 1 is done
                const activeProducts = [];
                if (data.product_is_optimus) activeProducts.push("optimus");
                if (data.product_is_pledgeplus) activeProducts.push("pledgeplus");
                if (data.product_is_polaris) activeProducts.push("polaris");
                if (data.product_is_polaris_lite) activeProducts.push("polaris_lite");
                if (activeProducts.length > 0) {
                    await fetchAllowedBrokers(activeProducts);
                }

                return data;
            }
        } catch (error) {
            console.error("Failed to load onboarding draft:", error);
            toast.error("Failed to load draft");
        } finally {
            setIsSaving(false);
        }
        return null;
    }, [fetchAllowedBrokers]);

    const deleteDraft = useCallback(async (id) => {
        try {
            await apiClient.delete(`${server.godseye.baseUrl}crm/onboarding/${id}`);
            toast.success("Draft deleted");
            return true;
        } catch (error) {
            console.error("Failed to delete draft:", error);
            toast.error("Failed to delete draft");
            return false;
        }
    }, []);

    return {
        draftId,
        currentStep,
        setCurrentStep,
        isSaving,
        basicInfo,
        setBasicInfo,
        legalities,
        setLegalities,
        selectedBroker,
        setSelectedBroker,
        allowedBrokers,
        resetOnboarding,
        createDraft,
        saveBasicInfo,
        saveLegalities,
        saveBrokerDetails,
        saveManualDocId,
        loadDraft,
        deleteDraft,
        fetchAllowedBrokers,
    };

}
